import {
  Component, Input, Output, EventEmitter,
  OnDestroy, OnInit, OnChanges, SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface PauseInfo {
  reason: string;
}

export const PAUSE_REASONS = [
  'Banheiro',
  'Pausa programada (almoço/café)',
  'Problema técnico',
  'Atendimento Odonto',
  'Agendamento Exames/POS',
  'Retirada de Exames',
  'Venda Cartão',
  'Treinamento',
  'Batidão',
  'Conversão PEP',
  'Sangria',
  'Outro',
];

export const EXPRESS_ALLOWED_REASONS = new Set([
  'Atendimento Odonto',
  'Venda Cartão',
  'Conversão PEP',
]);

export const OUTRO_MIN_CHARS = 10;

@Component({
  selector: 'app-countdown-timer',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './countdown-timer.component.html',
})
export class CountdownTimerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() totalSeconds = 5;
  @Input() autoPauseMinutes = 10;
  @Input() active = true;

  @Output() onTimerEnd    = new EventEmitter<void>();
  @Output() onPause       = new EventEmitter<PauseInfo>();
  @Output() onResume      = new EventEmitter<void>();
  @Output() onAutoPaused  = new EventEmitter<void>();

  remaining = 0;
  isPaused  = false;
  showPauseModal = false;
  selectedReason = '';
  customReason = '';
  readonly reasons = PAUSE_REASONS;
  readonly outroMinChars = OUTRO_MIN_CHARS;

  isExpressReason(reason: string): boolean {
    return EXPRESS_ALLOWED_REASONS.has(reason);
  }

  private ticker: any;
  private inactivityTimer: any;
  private lastActivityAt = Date.now();
  private pausedAt: number | null = null;

  ngOnInit(): void {
    this.reset();
    if (this.active) this.startTick();
    this.setupInactivityDetection();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active']) {
      changes['active'].currentValue ? this.startTick() : this.stopTick();
    }
    if (changes['totalSeconds'] && !changes['totalSeconds'].firstChange) {
      this.reset();
      if (this.active && !this.isPaused) this.startTick();
    }
  }

  ngOnDestroy(): void {
    this.stopTick();
    this.removeInactivityDetection();
  }

  // ── Controles externos ───────────────────────────────────────────────────

  restart(): void {
    this.isPaused = false;
    this.reset();
    this.startTick();
  }

  forceStop(): void {
    this.stopTick();
    this.remaining = 0;
  }

  // ── Tick ─────────────────────────────────────────────────────────────────

  private reset(): void {
    this.remaining = this.totalSeconds;
  }

  private startTick(): void {
    this.stopTick();
    if (this.isPaused || !this.active) return;
    this.ticker = setInterval(() => {
      if (this.remaining <= 0) {
        this.stopTick();
        this.onTimerEnd.emit();
        return;
      }
      this.remaining--;
    }, 1000);
  }

  private stopTick(): void {
    if (this.ticker) { clearInterval(this.ticker); this.ticker = null; }
  }

  get progressPct(): number {
    return this.totalSeconds > 0
      ? Math.round((this.remaining / this.totalSeconds) * 100)
      : 0;
  }

  get urgency(): 'normal' | 'warning' | 'critical' {
    if (this.progressPct > 50) return 'normal';
    if (this.progressPct > 20) return 'warning';
    return 'critical';
  }

  // ── Pausa ─────────────────────────────────────────────────────────────────

  openPauseModal(): void { this.showPauseModal = true; }

  closePauseModal(): void {
    this.showPauseModal = false;
    this.selectedReason = '';
    this.customReason = '';
  }

  confirmPause(): void {
    if (!this.selectedReason) return;
    if (this.selectedReason === 'Outro' && this.customReason.trim().length < this.outroMinChars) return;
    const reason = this.selectedReason === 'Outro'
      ? this.customReason.trim()
      : this.selectedReason;
    this.doPause(reason);
    this.closePauseModal();
  }

  // Atalho da barra: pausa com o motivo já escolhido, sem abrir o modal.
  pausarCom(reason: string): void {
    if (this.isPaused) return;
    this.doPause(reason);
  }

  resumeTimer(): void {
    if (!this.isPaused) return;
    this.isPaused = false;
    this.pausedAt = null;
    this.startTick();
    this.onResume.emit();
    this.resetInactivity();
  }

  private doPause(reason: string): void {
    this.stopTick();
    this.isPaused = true;
    this.pausedAt = Date.now();
    this.onPause.emit({ reason });
  }

  get pausedSeconds(): number {
    if (!this.pausedAt) return 0;
    return Math.floor((Date.now() - this.pausedAt) / 1000);
  }

  // ── Auto-pausa por inatividade ────────────────────────────────────────────

  private setupInactivityDetection(): void {
    this.trackActivity = this.trackActivity.bind(this);
    document.addEventListener('mousemove', this.trackActivity);
    document.addEventListener('keydown',   this.trackActivity);
    document.addEventListener('click',     this.trackActivity);
    this.inactivityTimer = setInterval(() => this.checkInactivity(), 30_000);
  }

  private removeInactivityDetection(): void {
    document.removeEventListener('mousemove', this.trackActivity);
    document.removeEventListener('keydown',   this.trackActivity);
    document.removeEventListener('click',     this.trackActivity);
    if (this.inactivityTimer) clearInterval(this.inactivityTimer);
  }

  private trackActivity(): void { this.lastActivityAt = Date.now(); }

  private resetInactivity(): void { this.lastActivityAt = Date.now(); }

  private checkInactivity(): void {
    if (this.isPaused || !this.active) return;
    const idleMin = (Date.now() - this.lastActivityAt) / 60_000;
    if (idleMin >= this.autoPauseMinutes) {
      this.doPause('Auto-pausa por inatividade');
      this.onAutoPaused.emit();
    }
  }
}
